import React, {
  useState,
  useMemo,
  useEffect,
} from "react";
import { formatISO } from "date-fns";
import { Box } from "@material-ui/core";
import AutoCompleteInput from "../../../../../components/AutoCompleteInput";
import { FilterDate, FilterCheckbox } from "../../../../../components/Filter";
import { checkboxDefaultInitialState } from "../../../../../components/Filter/checkbox";
import {
  fieldNames,
  filtersInitialState,
  productTypeOptionList,
  preOrderStatusOptionList,
} from "../../../../../constants/order";
import { Colors } from "../../../../../constants";
import { useDebounceEffect } from "../../../../../utils/hooks/useDebounceEffect";
import styled from "styled-components";

const appliedFiltersShape = (createdAt, productTypeList) => ({
  [fieldNames.CREATE_DATE]: createdAt,
  [fieldNames.PRODUCT_TYPE]: productTypeList,
});

const BoxWrapperStyle = styled.div`
  padding: 10px 0;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;

  &:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }
`;

const TitleStyle = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${Colors.TEXT_SECONDARY_BLACK};
`;

const Export = ({
  onExportPayloadChange,
  authorList = { list: [] },
  orderIds = [],
  orderIdsLoading = false,
  orderIdsHasMore = false,
  onOrderIdsLoadMore,
  onOrderIdsSearch,
  nfeNumbers = [],
  nfeNumbersLoading = false,
  preOrderIds = [],
  preOrderIdsLoading = false,
  preOrderIdsHasMore = false,
  onPreOrderIdsLoadMore,
  onPreOrderIdsSearch,
}) => {
  const [orderNumberListState, setOrderNumberListState] = useState([]);
  const [invoiceAndSeriesNumberListState, setInvoiceAndSeriesNumberListState] = useState([]);
  const [productTypeListState, setProductTypeListState] = useState(() => checkboxDefaultInitialState(productTypeOptionList));
  const [consolidatedPeriodState, setConsolidatedPeriodState] = useState(filtersInitialState[fieldNames.CREATE_DATE]);
  const [preOrderNumberListState, setPreOrderNumberListState] = useState([]);
  const [statusState, setStatusState] = useState([]);
  const [authorState, setAuthorState] = useState([]);
  const [preOrderPeriodState, setPreOrderPeriodState] = useState(filtersInitialState[fieldNames.CREATE_DATE]);
  const appliedFiltersConsolidated = appliedFiltersShape(
    consolidatedPeriodState,
    productTypeListState
  );
  const appliedFiltersPreOrder = {
    [fieldNames.CREATE_DATE]: preOrderPeriodState,
    [fieldNames.PRODUCT_TYPE]: productTypeListState,
  };

  const toDateString = (date) => date ? formatISO(date, { representation: 'date' }) : undefined;

  const buildPeriod = (period) => {
    if (!period || (period.from == null && period.to == null)) return undefined;
    const start = period.from ? toDateString(period.from) : undefined;
    const end = period.to ? toDateString(period.to) : undefined;
    if (start == null && end == null) return undefined;
    return { start, end };
  };

  const [orderSearchTerm, setOrderSearchTerm] = useState('');

  const handleOrderInputChange = (event, newInputValue) => {
    setOrderSearchTerm(newInputValue);
  };

  useDebounceEffect(
    () => {
      if (onOrderIdsSearch) {
        onOrderIdsSearch(orderSearchTerm);
      }
    },
    400,
    [orderSearchTerm],
  );

  const [preOrderSearchTerm, setPreOrderSearchTerm] = useState('');

  const handlePreOrderInputChange = (event, newInputValue) => {
    setPreOrderSearchTerm(newInputValue);
  };

  useDebounceEffect(
    () => {
      if (onPreOrderIdsSearch) {
        onPreOrderIdsSearch(preOrderSearchTerm);
      }
    },
    400,
    [preOrderSearchTerm],
  );

  const buildConsolidatedOrderFilters = ({
    orderNumberList,
    invoiceAndSeriesNumberList,
    productTypeList,
    period,
  }) => ({
    ...(orderNumberList?.length > 0 ? { orderNumberList } : {}),
    ...(invoiceAndSeriesNumberList?.length > 0
      ? { invoiceAndSeriesNumberList }
      : {}),
    ...(productTypeList
      ? (() => {
        const selected = Object.entries(productTypeList)
          .filter(([, checked]) => checked)
          .map(([value]) => String(value));
        return selected.length > 0 ? { productTypes: selected } : {};
      })()
      : {}),
    ...(buildPeriod(period) ? { period: buildPeriod(period) } : {}),
  });

  const buildPreOrderFilters = ({
    preOrderNumberList,
    status,
    author,
    period,
  }) => {
    const authorIds = author?.length
      ? author.map((a) => (typeof a === 'object' && a?.id != null ? a.id : a))
      : [];
    const statusValues = status?.length
      ? status.map((s) => (typeof s === 'object' && s?.value != null ? s.value : s))
      : [];
    return {
      ...(preOrderNumberList?.length > 0 ? { preOrderNumberList } : {}),
      ...(statusValues.length > 0 ? { status: statusValues } : {}),
      ...(authorIds.length > 0 ? { author: authorIds } : {}),
      ...(buildPeriod(period) ? { period: buildPeriod(period) } : {}),
    };
  };

  const consolidatedOrderFilters = useMemo(
    () =>
      buildConsolidatedOrderFilters({
        orderNumberList: orderNumberListState,
        invoiceAndSeriesNumberList: invoiceAndSeriesNumberListState,
        productTypeList: productTypeListState,
        period: consolidatedPeriodState,
      }),
    [
      orderNumberListState,
      invoiceAndSeriesNumberListState,
      productTypeListState,
      consolidatedPeriodState,
    ]
  );

  const preOrderFilters = useMemo(
    () =>
      buildPreOrderFilters({
        preOrderNumberList: preOrderNumberListState,
        status: statusState,
        author: authorState,
        period: preOrderPeriodState,
      }),
    [preOrderNumberListState, statusState, authorState, preOrderPeriodState]
  );

  useEffect(() => {
    if (onExportPayloadChange) {
      onExportPayloadChange({ consolidatedOrderFilters, preOrderFilters });
    }
  }, [onExportPayloadChange, consolidatedOrderFilters, preOrderFilters]);

  return (
    <Box style={{ padding: "20px 0px", width: "720px" }}>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px 24px",
          gap: "8px",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "50%",
          }}
        >
          <TitleStyle>Pedido Consolidado</TitleStyle>
          <AutoCompleteInput
            options={orderIds.map(String)}
            value={orderNumberListState}
            onChange={(e, newValue) => setOrderNumberListState(newValue)}
            onInputChange={handleOrderInputChange}
            onLoadMore={onOrderIdsLoadMore}
            hasMore={orderIdsHasMore}
            loading={orderIdsLoading}
            getOptionSelected={(option, val) => option === val}
            placeholder="Nº do pedido"
          />
          <AutoCompleteInput
            options={nfeNumbers}
            value={invoiceAndSeriesNumberListState}
            onChange={(e, newValue) => setInvoiceAndSeriesNumberListState(newValue)}
            loading={nfeNumbersLoading}
            placeholder="Nota fiscal cliente"
          />
          <BoxWrapperStyle>
            <FilterCheckbox
              name={fieldNames.PRODUCT_TYPE}
              label="Tipo de produto"
              optionList={productTypeOptionList}
              value={productTypeListState}
              handleOutput={setProductTypeListState}
              appliedFilters={appliedFiltersConsolidated}
              fieldNames={fieldNames}
              inputStyleIcon
              useModalSpacing
            />
          </BoxWrapperStyle>
          <BoxWrapperStyle>
            <FilterDate
              name={fieldNames.CREATE_DATE}
              label="Período"
              value={consolidatedPeriodState}
              handleOutput={setConsolidatedPeriodState}
              appliedFilters={appliedFiltersConsolidated}
              fieldNames={fieldNames}
              dropdownInModal
              singleMode
              useModalSpacing
            />
          </BoxWrapperStyle>
        </Box>

        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "50%",
          }}
        >
          <TitleStyle>Pré-Pedido</TitleStyle>
          <AutoCompleteInput
            options={preOrderIds.map(String)}
            value={preOrderNumberListState}
            onChange={(e, newValue) => setPreOrderNumberListState(newValue)}
            onInputChange={handlePreOrderInputChange}
            onLoadMore={onPreOrderIdsLoadMore}
            hasMore={preOrderIdsHasMore}
            loading={preOrderIdsLoading}
            getOptionSelected={(option, val) => option === val}
            placeholder="Nº do Pré-Pedido"
          />
          <AutoCompleteInput
            options={preOrderStatusOptionList}
            value={statusState}
            onChange={(e, newValue) => setStatusState(newValue)}
            getOptionLabel={(option) => (typeof option === 'object' && option?.label != null ? option.label : String(option ?? ''))}
            placeholder="Status"
          />
          <AutoCompleteInput
            options={authorList.list ?? []}
            value={authorState}
            onChange={(e, newValue) => setAuthorState(newValue)}
            getOptionLabel={(option) => (typeof option === 'object' && option?.name != null ? option.name : String(option ?? ''))}
            placeholder="Autor"
          />
          <BoxWrapperStyle>
            <FilterDate
              name={fieldNames.CREATE_DATE}
              label="Período"
              value={preOrderPeriodState}
              handleOutput={setPreOrderPeriodState}
              appliedFilters={appliedFiltersPreOrder}
              fieldNames={fieldNames}
              dropdownInModal
              singleMode
              useModalSpacing
            />
          </BoxWrapperStyle>
        </Box>
      </Box>
    </Box>
  );
};

export default Export;
